import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDate, formatDateTime } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ClipboardList, Plus, Trash2, CheckCircle2, RotateCcw, GripVertical, Inbox, Paperclip, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon, Loader2, X, Pencil, Check, History } from 'lucide-react';
import { useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function IconoAdjunto({ adj }) {
  const mime = adj.mime || '';
  const n = (adj.name || '').toLowerCase();
  if (mime === 'application/pdf' || n.endsWith('.pdf')) return <FileText className='w-3 h-3 text-red-600' />;
  if (mime.startsWith('image/')) return <ImageIcon className='w-3 h-3 text-purple-600' />;
  if (/\.(xlsx?|csv)$/.test(n) || mime.includes('sheet') || mime.includes('excel')) return <FileSpreadsheet className='w-3 h-3 text-emerald-600' />;
  return <FileIcon className='w-3 h-3 text-muted-foreground' />;
}

/**
 * Tareas dirección — pizarra de los administradores de la clínica.
 *
 * Izquierda: panel GENERAL, donde cualquiera apunta tareas con su prioridad.
 * Derecha: el panel PROPIO de cada administrador. Quien vaya a hacer una tarea
 * la ARRASTRA del general a su panel (o pulsa "La cojo"). Todos ven todo.
 *
 * Edición (2026-09-08): una tarea ya apuntada se puede corregir
 * entera —texto, prioridad, panel y estado— desde el lápiz de la fila o de la
 * tarjeta, por cualquiera del equipo. Cada cambio queda en el historial de la
 * tarea y en Control rastreo (quién, cuándo, valor anterior → nuevo).
 */

// Rediseño 2026-07-27: fuera la barra de color a la izquierda (leía a ticket de
// incidencias) y los chips en MAYÚSCULAS con borde. La prioridad va en una
// pastilla suave sin borde, y solo la ALTA tiñe la tarjeta — si todo destaca,
// no destaca nada.
const PRIORIDADES = [
  {
    value: 'alta', label: 'Alta', dot: 'bg-red-600',
    chip: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200',
    tarjeta: 'bg-red-50/40 dark:bg-red-950/10 ring-red-200 dark:ring-red-900/40',
  },
  {
    value: 'media', label: 'Media', dot: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
    tarjeta: 'bg-white dark:bg-card ring-border',
  },
  {
    value: 'baja', label: 'Baja', dot: 'bg-emerald-600',
    chip: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
    tarjeta: 'bg-white dark:bg-card ring-border',
  },
];
const prioridadDe = (v) => PRIORIDADES.find((p) => p.value === v) || PRIORIDADES[1];
const ORDEN_PRIORIDAD = { alta: 0, media: 1, baja: 2 };
// Columnas de la fila de paneles personales según cuántos hay (clases
// estáticas, para que Tailwind las genere); con más de tres, se reparten en
// filas de tres.
const COLUMNAS_PANELES = { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3' };

function TarjetaTarea({ tarea, esMia, onHecha, onBorrar, onCoger, onEditar, arrastrable, onCambio, onArrastre, arrastrandoId, aliasPorId = {} }) {
  const pr = prioridadDe(tarea.prioridad);
  const adjInputRef = useRef(null);
  const [subiendoAdj, setSubiendoAdj] = React.useState(false);

  const subirAdjuntos = async (files) => {
    const lista = Array.from(files || []).filter(Boolean);
    if (!lista.length) return;
    setSubiendoAdj(true);
    for (const f of lista) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        await api.post(`/api/tareas-direccion/${tarea.id}/adjuntos`, fd);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'no se pudo adjuntar'}`);
      }
    }
    setSubiendoAdj(false);
    onCambio?.();
  };

  const borrarAdjunto = async (adj) => {
    if (!window.confirm(`¿Quitar «${adj.name}»?`)) return;
    try {
      await api.delete(`/api/tareas-direccion/${tarea.id}/adjuntos/${adj.id}`);
      onCambio?.();
    } catch (e) { toast.error('No se pudo quitar'); }
  };
  return (
    <div
      draggable={arrastrable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/tarea-id', tarea.id);
        e.dataTransfer.effectAllowed = 'move';
        // Miniatura bajo el cursor: la tarjeta entera estorbaba para apuntar
        // al panel; una pastilla pequeña hace obvio dónde se va a soltar.
        const ghost = document.createElement('div');
        ghost.textContent = tarea.texto.length > 38 ? `${tarea.texto.slice(0, 38)}…` : tarea.texto;
        ghost.style.cssText = 'position:absolute;top:0;left:-9999px;max-width:220px;padding:5px 10px;background:#2E6E5E;color:#fff;font-size:12px;font-weight:600;border-radius:999px;box-shadow:0 6px 16px rgba(0,0,0,.3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
        document.body.appendChild(ghost);
        try { e.dataTransfer.setDragImage(ghost, 16, 16); } catch {}
        setTimeout(() => ghost.remove(), 0);
        onArrastre?.(tarea.id);
      }}
      onDragEnd={() => onArrastre?.(null)}
      className={`group rounded-xl ring-1 p-3 transition-[background-color,box-shadow,transform] duration-150
        ${tarea.hecha ? 'bg-muted/40 dark:bg-muted/10 ring-border' : pr.tarjeta}
        ${arrastrable ? 'cursor-grab active:cursor-grabbing hover:ring-[#2E6E5E]/40' : ''}
        ${arrastrandoId === tarea.id ? 'opacity-40 scale-95 max-h-9 overflow-hidden' : ''}`}
      data-testid="tarea-card"
    >
      <div className="flex items-start gap-2">
        {arrastrable && (
          <GripVertical className="w-4 h-4 text-muted-foreground/40 shrink-0 mt-0.5
            transition-colors duration-150 group-hover:text-muted-foreground" />
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm whitespace-pre-wrap break-words leading-snug ${
            tarea.hecha ? 'line-through text-muted-foreground' : 'text-[#13231E]'
          }`}>
            {tarea.texto}
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {/* Sin mayúsculas ni borde: la prioridad se lee por color y ya. */}
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${pr.chip}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${pr.dot}`} /> {pr.label}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {aliasPorId[tarea.created_by] || tarea.created_by_name} · {formatDate((tarea.created_at || '').slice(0, 10))}
            </span>
            {/* Quién cambió el texto o la prioridad por última vez: se ve sin abrirla. */}
            {tarea.editada_by_name && (
              <span className="text-[11px] text-muted-foreground"
                title={tarea.editada_at ? `Editada el ${formatDateTime(tarea.editada_at)}` : 'Editada'}
                data-testid="tarea-editada-por">
                · editada por {tarea.editada_by_name}
              </span>
            )}
            {tarea.hecha && tarea.hecha_by_name && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 dark:text-emerald-300">
                <CheckCircle2 className="w-3.5 h-3.5" /> {tarea.hecha_by_name}
              </span>
            )}
          </div>
          {(tarea.adjuntos || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {tarea.adjuntos.map((adj) => (
                <span key={adj.id}
                  className="inline-flex items-center gap-1.5 text-[11px] rounded-lg pl-2 pr-1 py-1
                    bg-[rgba(46,110,94,0.07)] hover:bg-[rgba(46,110,94,0.12)]
                    transition-colors duration-150 group/adj">
                  <IconoAdjunto adj={adj} />
                  <a
                    href={`${BACKEND_URL}/api/tareas-direccion/${tarea.id}/adjuntos/${adj.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline max-w-[150px] truncate text-[#13231E]"
                    title={adj.name}
                  >
                    {adj.name}
                  </a>
                  {/* La × solo al pasar por encima: antes estaba siempre a la
                      vista y llenaba la tarjeta de aspas. */}
                  <button
                    type="button"
                    className="w-5 h-5 rounded inline-flex items-center justify-center shrink-0
                      text-muted-foreground opacity-0 group-hover/adj:opacity-100
                      focus-visible:opacity-100 hover:text-red-600 hover:bg-red-50
                      transition-opacity duration-150"
                    aria-label={`Quitar ${adj.name}`}
                    title="Quitar adjunto"
                    onClick={() => borrarAdjunto(adj)}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Botones de 32px: los de 24 se fallaban con el dedo. En escritorio
            aparecen al pasar; en táctil están siempre, que no hay hover. */}
        <div className="flex items-center gap-0.5 shrink-0
          opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100
          transition-opacity duration-150">
          {onCoger && !tarea.hecha && (
            <Button size="sm" variant="outline" className="h-8 text-xs px-2.5"
              onClick={() => onCoger(tarea)} data-testid="tarea-coger">
              La cojo
            </Button>
          )}
          {onEditar && (
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
              aria-label="Editar tarea" title="Editar (texto, prioridad, panel, estado)"
              onClick={() => onEditar(tarea)} data-testid="tarea-editar">
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {esMia && (
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
              aria-label={tarea.hecha ? 'Volver a pendiente' : 'Marcar hecha'}
              title={tarea.hecha ? 'Volver a pendiente' : 'Marcar hecha'}
              onClick={() => onHecha(tarea)}>
              {tarea.hecha ? <RotateCcw className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
            aria-label="Adjuntar fichero" title="Adjuntar fichero (foto, PDF, Excel…)"
            disabled={subiendoAdj}
            onClick={() => adjInputRef.current?.click()} data-testid="tarea-adjuntar">
            {subiendoAdj ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          </Button>
          <input
            ref={adjInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { subirAdjuntos(e.target.files); e.target.value = ''; }}
          />
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive"
            aria-label="Eliminar tarea" title="Eliminar"
            onClick={() => onBorrar(tarea)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Fila del resumen general (2026-07-27). El cuadro de arriba dejó de listar
 * solo lo que nadie había cogido — decía «Nada pendiente» con cinco tareas
 * repartidas justo debajo, que era mentira a primera vista. Ahora lista TODO
 * lo que hay por hacer, ordenado por prioridad, diciendo de quién es, y se
 * puede marcar o borrar sin bajar al panel de cada una.
 */
function FilaResumen({ tarea, onHecha, onBorrar, onEditar, onArrastre, arrastrandoId }) {
  const pr = prioridadDe(tarea.prioridad);
  const duena = tarea.asignada_a_name;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/tarea-id', tarea.id);
        e.dataTransfer.effectAllowed = 'move';
        onArrastre?.(tarea.id);
      }}
      onDragEnd={() => onArrastre?.(null)}
      className={`group flex items-center gap-3 rounded-lg px-2 py-2 -mx-1
        cursor-grab active:cursor-grabbing
        transition-colors duration-150 hover:bg-muted/50
        ${arrastrandoId === tarea.id ? 'opacity-40' : ''}`}
      data-testid="tarea-resumen-fila"
    >
      {/* Se arrastran también desde aquí: repartir una tarea no debe obligar a
          bajar al panel de nadie. */}
      <GripVertical className="w-4 h-4 shrink-0 text-muted-foreground/30
        transition-colors duration-150 group-hover:text-muted-foreground" />
      <span className={`w-2 h-2 rounded-full shrink-0 ${pr.dot}`}
        title={`Prioridad ${pr.label.toLowerCase()}`} />
      <span className={`flex-1 min-w-0 text-sm truncate ${
        tarea.hecha ? 'line-through text-muted-foreground' : 'text-[#13231E]'
      }`} title={tarea.texto}>
        {tarea.texto}
      </span>
      {/* De quién es. Sin repartir se marca aparte: es lo que hay que mover. */}
      <span className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${
        duena
          ? 'bg-[rgba(46,110,94,0.10)] text-[#1E4A3F] dark:text-[#8FCBB4]'
          : 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
      }`}>
        {duena || 'sin repartir'}
      </span>
      <div className="flex items-center gap-0.5 shrink-0
        opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100
        transition-opacity duration-150">
        {onEditar && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
            aria-label={`Editar: ${tarea.texto}`} title="Editar (texto, prioridad, panel, estado)"
            onClick={() => onEditar(tarea)} data-testid="tarea-resumen-editar">
            <Pencil className="w-4 h-4" />
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
          aria-label={tarea.hecha ? `Volver a pendiente: ${tarea.texto}` : `Marcar hecha: ${tarea.texto}`}
          title={tarea.hecha ? 'Volver a pendiente' : 'Marcar hecha'}
          onClick={() => onHecha(tarea)} data-testid="tarea-resumen-hecha">
          {tarea.hecha ? <RotateCcw className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
        </Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive"
          aria-label={`Eliminar: ${tarea.texto}`} title="Eliminar"
          onClick={() => onBorrar(tarea)} data-testid="tarea-resumen-borrar">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Diálogo de edición (2026-09-08): «las tareas de dirección no se
 * pueden editar una vez creadas». Hasta ahora una tarea apuntada solo se
 * podía mover, marcar hecha o borrar; el texto y la prioridad quedaban como
 * se escribieron. Aquí se cambia CUALQUIER campo —texto, prioridad, panel y
 * estado— y debajo se ve el historial: quién cambió qué y cuándo.
 *
 * Solo se envían los campos que de verdad cambian: así el rastro no se llena
 * de «guardados» sin cambios.
 */
function EditarTareaDialog({ tarea, admins, aliasPorId = {}, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [historial, setHistorial] = useState(null); // null = cargando
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!tarea) { setForm(null); setHistorial(null); return; }
    setForm({
      texto: tarea.texto || '',
      prioridad: tarea.prioridad || 'media',
      asignada_a: tarea.asignada_a || '',
      hecha: !!tarea.hecha,
    });
    setHistorial(null);
    let vivo = true;
    api.get(`/api/tareas-direccion/${tarea.id}/historial`)
      .then((r) => { if (vivo) setHistorial(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (vivo) setHistorial([]); });
    return () => { vivo = false; };
  }, [tarea]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /** Solo lo que difiere de la tarea tal como está. */
  const cambios = () => {
    const out = {};
    if (form.texto.trim() !== (tarea.texto || '')) out.texto = form.texto.trim();
    if (form.prioridad !== (tarea.prioridad || 'media')) out.prioridad = form.prioridad;
    if ((form.asignada_a || null) !== (tarea.asignada_a || null)) out.asignada_a = form.asignada_a || null;
    if (form.hecha !== !!tarea.hecha) out.hecha = form.hecha;
    return out;
  };

  const guardar = async () => {
    if (!form.texto.trim()) { toast.error('La tarea no puede quedar vacía'); return; }
    const payload = cambios();
    if (Object.keys(payload).length === 0) { onOpenChange(false); return; }
    setGuardando(true);
    try {
      await api.put(`/api/tareas-direccion/${tarea.id}`, payload);
      toast.success('Tarea actualizada');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la tarea');
    } finally { setGuardando(false); }
  };

  const chipEstado = (activo, extra) => `inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5
    rounded-full transition-colors duration-150 focus-visible:outline-none
    focus-visible:ring-2 focus-visible:ring-[#2E6E5E] ${activo ? extra : 'bg-muted/60 text-muted-foreground hover:bg-muted'}`;

  return (
    <Dialog open={!!tarea} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar tarea</DialogTitle>
          <DialogDescription>
            {tarea?.created_by_name || tarea?.created_by ? (
              <>Apuntada por <strong>{aliasPorId[tarea.created_by] || tarea.created_by_name}</strong>
                {tarea.created_at ? ` el ${formatDate(tarea.created_at.slice(0, 10))}` : ''}. </>
            ) : null}
            Tus cambios quedan registrados a tu nombre.
          </DialogDescription>
        </DialogHeader>

        {form && tarea && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="editar-tarea-texto" className="text-xs font-medium text-muted-foreground">Tarea</label>
              <Textarea
                id="editar-tarea-texto"
                value={form.texto}
                onChange={(e) => set('texto', e.target.value)}
                className="min-h-[88px] text-sm"
                data-testid="editar-tarea-texto"
              />
            </div>

            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground">Prioridad</div>
              <div className="flex gap-1.5" role="group" aria-label="Prioridad de la tarea">
                {PRIORIDADES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => set('prioridad', p.value)}
                    aria-pressed={form.prioridad === p.value}
                    className={chipEstado(form.prioridad === p.value, p.chip)}
                    data-testid={`editar-tarea-prioridad-${p.value}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${form.prioridad === p.value ? p.dot : 'bg-muted-foreground/40'}`} />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="editar-tarea-panel" className="text-xs font-medium text-muted-foreground">Panel</label>
                <select
                  id="editar-tarea-panel"
                  value={form.asignada_a}
                  onChange={(e) => set('asignada_a', e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="editar-tarea-panel"
                >
                  <option value="">General (sin repartir)</option>
                  {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground">Estado</div>
                <div className="flex gap-1.5" role="group" aria-label="Estado de la tarea">
                  <button type="button" onClick={() => set('hecha', false)} aria-pressed={!form.hecha}
                    className={chipEstado(!form.hecha, 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200')}
                    data-testid="editar-tarea-estado-pendiente">
                    Pendiente
                  </button>
                  <button type="button" onClick={() => set('hecha', true)} aria-pressed={form.hecha}
                    className={chipEstado(form.hecha, 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200')}
                    data-testid="editar-tarea-estado-hecha">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Hecha
                  </button>
                </div>
              </div>
            </div>

            {/* Historial: quién cambió qué (valor anterior → nuevo) y cuándo. */}
            <div className="rounded-lg bg-muted/40 p-2.5" data-testid="editar-tarea-historial">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground mb-1.5">
                <History className="w-3.5 h-3.5" /> Historial de cambios
              </div>
              {historial === null ? (
                <div className="text-xs text-muted-foreground">Cargando…</div>
              ) : historial.length === 0 ? (
                <div className="text-xs text-muted-foreground">Sin cambios desde que se apuntó.</div>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {historial.map((h, i) => (
                    <div key={h.id || i} className="text-xs" data-testid="editar-tarea-historial-item">
                      <span className="tabular-nums text-muted-foreground">{formatDateTime(h.ts)}</span>
                      {' · '}<span className="font-medium">{h.user_name || '—'}</span>
                      <div className="pl-2 space-y-0.5">
                        {(h.cambios || []).map((c, j) => (
                          <div key={j} className="text-muted-foreground">
                            <span className="font-medium text-foreground/80">{c.etiqueta || c.campo}:</span>{' '}
                            <span className="line-through decoration-red-400/60 break-words">{c.antes}</span>
                            {' → '}
                            <span className="text-emerald-800 dark:text-emerald-400 break-words">{c.despues}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !form}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="editar-tarea-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Panel({ titulo, chip, children, onDropTarea, resaltado, testid, dropActivo }) {
  const [sobre, setSobre] = useState(false);
  return (
    <div
      onDragOver={onDropTarea ? (e) => { e.preventDefault(); setSobre(true); } : undefined}
      onDragLeave={onDropTarea ? () => setSobre(false) : undefined}
      onDrop={onDropTarea ? (e) => {
        e.preventDefault();
        setSobre(false);
        const id = e.dataTransfer.getData('text/tarea-id');
        if (id) onDropTarea(id);
      } : undefined}
      className={`cp-card p-3 transition-all ${sobre ? 'ring-2 ring-[#2E6E5E] bg-[rgba(46,110,94,0.05)]' : ''} ${resaltado ? 'border-[#2E6E5E]/40' : ''} ${dropActivo && !sobre ? 'outline-dashed outline-2 outline-[#2E6E5E]/35' : ''}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-display font-semibold text-[#13231E]">{titulo}</span>
        {chip}
      </div>
      <div className="space-y-2 min-h-[60px]">{children}</div>
    </div>
  );
}

export default function TareasDireccionPage() {
  const [datos, setDatos] = useState(null); // {tareas, admins, yo}
  const [texto, setTexto] = useState('');
  const [prioridad, setPrioridad] = useState('media');
  const [creando, setCreando] = useState(false);
  // id de la tarjeta que se está arrastrando (para atenuarla y marcar destinos)
  const [arrastrandoId, setArrastrandoId] = useState(null);
  // Tarea abierta en el diálogo de edición (null = cerrado).
  const [editando, setEditando] = useState(null);

  const cargar = useCallback(() => {
    api.get('/api/tareas-direccion')
      .then((r) => setDatos(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudo cargar la pizarra'));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  // La pizarra es compartida: se refresca sola para ver lo que apuntan los demás.
  useEffect(() => {
    const t = () => { if (!document.hidden) cargar(); };
    const id = setInterval(t, 30000);
    window.addEventListener('focus', t);
    return () => { clearInterval(id); window.removeEventListener('focus', t); };
  }, [cargar]);

  const crear = async () => {
    if (!texto.trim()) return;
    setCreando(true);
    try {
      await api.post('/api/tareas-direccion', { texto: texto.trim(), prioridad });
      setTexto('');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear');
    } finally { setCreando(false); }
  };

  const asignar = async (tareaId, adminId) => {
    try {
      await api.put(`/api/tareas-direccion/${tareaId}`, { asignada_a: adminId });
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo mover');
    }
  };

  const alternarHecha = async (t) => {
    try {
      await api.put(`/api/tareas-direccion/${t.id}`, { hecha: !t.hecha });
      cargar();
    } catch (e) { toast.error('No se pudo cambiar'); }
  };

  const borrar = async (t) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await api.delete(`/api/tareas-direccion/${t.id}`);
      cargar();
    } catch (e) { toast.error('No se pudo eliminar'); }
  };

  if (!datos) return <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>;

  const { tareas, admins, yo } = datos;
  // Nombre a mostrar por autor: el alias de la pizarra si lo hay.
  const aliasPorId = Object.fromEntries(admins.map((a) => [a.id, a.name]));
  const ordenar = (ts) => [...ts].sort((a, b) =>
    (a.hecha === b.hecha ? (ORDEN_PRIORIDAD[a.prioridad] ?? 1) - (ORDEN_PRIORIDAD[b.prioridad] ?? 1) : a.hecha ? 1 : -1)
    || (b.created_at || '').localeCompare(a.created_at || ''));
  const generales = ordenar(tareas.filter((t) => !t.asignada_a));
  // Resumen del cuadro de arriba: TODO lo que hay, repartido o no, con las
  // pendientes primero y por prioridad. Sin repartir arriba del todo dentro de
  // su prioridad, que es lo que hay que mover.
  const resumen = ordenar(tareas).sort((a, b) =>
    (a.hecha === b.hecha ? 0 : a.hecha ? 1 : -1)
    || (!!a.asignada_a === !!b.asignada_a ? 0 : a.asignada_a ? 1 : -1)
    || (ORDEN_PRIORIDAD[a.prioridad] ?? 1) - (ORDEN_PRIORIDAD[b.prioridad] ?? 1));
  const dePanel = (adminId) => ordenar(tareas.filter((t) => t.asignada_a === adminId));
  // Orden de los paneles: alfabético, y el tuyo primero — así cada
  // administrador abre y ve el suyo a la izquierda sin buscarlo.
  //
  // Antes había un orden fijo por nombre de pila con los tres de una clínica
  // concreta escritos en el código, así que en cualquier otra copia el
  // resultado era arbitrario.
  const paneles = [...admins].sort((a, b) => {
    if (a.id === yo) return -1;
    if (b.id === yo) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });
  const pendientes = (ts) => ts.filter((t) => !t.hecha).length;

  return (
    <div>
      <PageHeader
        title="Tareas dirección"
        subtitle="Pizarra de los administradores: se apunta en el panel general y cada uno arrastra a su panel lo que va a hacer."
      />

      {/* Maquetación en dos filas (2026-07-27): el panel general ocupa todo
          el ancho arriba y los personales van debajo, en fila. Antes iban
          apilados en una columna estrecha a la derecha y quedaban muy justos. */}
      <div className="space-y-4">
        {/* Panel general */}
        <Panel
          titulo="Todo lo que hay por hacer"
          chip={(
            <span className="text-xs text-muted-foreground">
              {pendientes(resumen)} pendientes
              {pendientes(generales) > 0 && (
                <span className="ml-2 text-amber-700 dark:text-amber-300 font-medium">
                  · {pendientes(generales)} sin repartir
                </span>
              )}
            </span>
          )}
          onDropTarea={(id) => asignar(id, null)}
          dropActivo={!!arrastrandoId}
          testid="panel-general"
        >
          {/* Alta rápida */}
          <div className="rounded-xl bg-[rgba(46,110,94,0.04)] ring-1 ring-[#2E6E5E]/15
            p-3 mb-3 transition-shadow duration-150 focus-within:ring-2 focus-within:ring-[#2E6E5E]/40">
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); crear(); } }}
              placeholder="Escribe una tarea… (Enter la añade)"
              className="min-h-[44px] resize-none border-0 bg-transparent p-1 focus-visible:ring-0 text-sm"
              data-testid="tarea-nueva-texto"
            />
            <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
              {/* Selector de prioridad: el elegido va sólido, los otros en
                  gris — antes iban los tres de color al 50% de opacidad y no
                  se veía cuál estaba puesto. */}
              <div className="flex gap-1.5" role="group" aria-label="Prioridad de la tarea">
                {PRIORIDADES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPrioridad(p.value)}
                    aria-pressed={prioridad === p.value}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1
                      rounded-full transition-colors duration-150
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E] ${
                      prioridad === p.value
                        ? p.chip
                        : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                    }`}
                    data-testid={`tarea-prioridad-${p.value}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${prioridad === p.value ? p.dot : 'bg-muted-foreground/40'}`} />
                    {p.label}
                  </button>
                ))}
              </div>
              <Button size="sm" onClick={crear} disabled={creando || !texto.trim()}
                className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="tarea-crear">
                <Plus className="w-4 h-4 mr-1" /> Añadir
              </Button>
            </div>
          </div>

          {resumen.length === 0 ? (
            <EmptyState icon={Inbox} title="Nada pendiente"
              description="No hay ninguna tarea apuntada. Escribe la primera arriba." />
          ) : (
            <div className="divide-y divide-border/60">
              {resumen.map((t) => (
                <FilaResumen
                  key={t.id}
                  tarea={t}
                  onHecha={alternarHecha}
                  onBorrar={borrar}
                  onEditar={setEditando}
                  onArrastre={setArrastrandoId}
                  arrastrandoId={arrastrandoId}
                />
              ))}
            </div>
          )}
        </Panel>

        {/* Un panel personal por administrador, en fila debajo del general
            (cuántos, lo dice el servidor). Cualquiera puede arrastrar
            cualquier tarea a cualquier panel. En móvil se apilan solos. */}
        <div className={`grid grid-cols-1 ${COLUMNAS_PANELES[Math.min(paneles.length, 3)] || ''} gap-4 items-start`}>
          {paneles.map((a) => {
            const esElMio = a.id === yo;
            const ts = dePanel(a.id);
            return (
              <Panel
                key={a.id}
                titulo={esElMio ? `${a.name} (yo)` : a.name}
                chip={<span className="text-xs text-muted-foreground">{pendientes(ts)} pendientes</span>}
                onDropTarea={(id) => asignar(id, a.id)}
                dropActivo={!!arrastrandoId}
                resaltado={esElMio}
                testid={`panel-${esElMio ? 'mio' : 'otro'}`}
              >
                {ts.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-2">
                    {esElMio ? 'Arrastra aquí (o pulsa «La cojo») lo que vayas a hacer.' : 'Sin tareas.'}
                  </div>
                ) : (
                  ts.map((t) => (
                    <TarjetaTarea
                      key={t.id}
                      tarea={t}
                      esMia
                      arrastrable
                      onHecha={alternarHecha}
                      onBorrar={borrar}
                      onEditar={setEditando}
                      onCambio={cargar}
                      onArrastre={setArrastrandoId}
                      arrastrandoId={arrastrandoId}
                      aliasPorId={aliasPorId}
                    />
                  ))
                )}
              </Panel>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
        <ClipboardList className="w-3.5 h-3.5" />
        Las tarjetas se mueven arrastrándolas entre los paneles; para devolver una al general, arrástrala de vuelta.
        El lápiz abre la tarea para cambiar el texto, la prioridad, el panel o el estado.
      </p>

      <EditarTareaDialog
        tarea={editando}
        admins={admins}
        aliasPorId={aliasPorId}
        onOpenChange={(abierto) => { if (!abierto) setEditando(null); }}
        onSaved={cargar}
      />
    </div>
  );
}
